import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { schemas, type ClientDetails, type CreateClientRequest } from '@kreart/shared';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { FormErrorBanner } from '@/components/form';
import { useCreateClient } from '@/features/clients/hooks';
import { errorKeyFor } from '@/features/clients/errorMessages';
import { ClientFormPanel, type ClientFormValues } from './ClientFormPanel';

interface ClientCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (client: ClientDetails) => void;
}

const DEFAULT_VALUES: ClientFormValues = {
  firstName: '',
  lastName: '',
  company: '',
  brand: '',
  codClient: '',
  contactName: '',
  email: '',
  phone: '',
  taxId: '',
  regCom: '',
  vatPayer: false,
  iban: '',
  bank: '',
  billingStreet: '',
  billingCity: '',
  billingCounty: '',
  billingCountry: 'RO',
  notes: '',
};

export function ClientCreateDialog({ open, onClose, onCreated }: ClientCreateDialogProps) {
  const { t } = useTranslation();
  const createMut = useCreateClient();
  const wasOpenRef = useRef(false);
  const form = useForm<ClientFormValues>({
    resolver: zodResolver(schemas.createClientRequest),
    defaultValues: DEFAULT_VALUES,
  });

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      form.reset(DEFAULT_VALUES);
      createMut.reset();
    }
    wasOpenRef.current = open;
  }, [createMut, form, open]);

  const closeIfIdle = () => {
    if (!createMut.isPending) onClose();
  };

  const onSubmit = async (values: ClientFormValues) => {
    const body: CreateClientRequest = {
      firstName: values.firstName || undefined,
      lastName: values.lastName,
      company: values.company || undefined,
      brand: values.brand || undefined,
      codClient: values.codClient || undefined,
      contactName: values.contactName || undefined,
      email: values.email || undefined,
      phone: values.phone || undefined,
      taxId: values.taxId || undefined,
      regCom: values.regCom || undefined,
      vatPayer: values.vatPayer,
      iban: values.iban || undefined,
      bank: values.bank || undefined,
      billingStreet: values.billingStreet || undefined,
      billingCity: values.billingCity || undefined,
      billingCounty: values.billingCounty || undefined,
      billingCountry: values.billingCountry || 'RO',
      notes: values.notes || undefined,
    };
    try {
      const created = await createMut.mutateAsync(body);
      onCreated?.(created);
      onClose();
    } catch {
      /* surfaced via FormErrorBanner */
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) closeIfIdle();
      }}
    >
      <DialogContent
        hideClose
        className="max-h-[92vh] w-[min(96vw,1186px)] max-w-none overflow-y-auto border-0 bg-transparent p-0 shadow-none"
      >
        <DialogTitle className="sr-only">{t('clients.create.title')}</DialogTitle>
        <ClientFormPanel
          form={form}
          title={t('clients.create.title')}
          submitLabel={t('clients.form.saveClientData')}
          isSubmitting={createMut.isPending}
          addressActionLabel={t('clients.addresses.addInline')}
          addressActionDisabled
          onCancel={closeIfIdle}
          onSubmit={onSubmit}
        >
          <FormErrorBanner error={createMut.error} resolveKey={errorKeyFor} />
        </ClientFormPanel>
      </DialogContent>
    </Dialog>
  );
}
